import { useEffect, useRef } from 'react';

// Per-page list UI state (search / filters / scroll) persisted in sessionStorage,
// so navigating away and coming back keeps the same view. Scoped per page by `name`.

const storageKey = (name) => `listState:${name}`;

export const readListState = (name) => {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey(name))) || {};
  } catch {
    return {};
  }
};

export const persistListState = (name, partial) => {
  try {
    const cur = readListState(name);
    sessionStorage.setItem(storageKey(name), JSON.stringify({ ...cur, ...partial }));
  } catch {
    /* sessionStorage unavailable (private mode, etc.) — ignore */
  }
};

// Saves window scroll position when leaving the page and restores it once the
// page is ready (data loaded). Works on desktop & mobile (both scroll the window).
export const useListScroll = (name, ready) => {
  const restored = useRef(false);

  // Save scroll position on unmount (covers back, menu nav, row click — anything)
  useEffect(() => {
    return () => {
      try { persistListState(name, { scrollY: window.scrollY }); } catch { /* ignore */ }
    };
  }, [name]);

  // Restore once after the list has rendered
  useEffect(() => {
    if (!ready || restored.current) return;
    const y = readListState(name).scrollY;
    if (typeof y === 'number' && y > 0) {
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
    restored.current = true;
  }, [ready, name]);
};
